import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import { Card, Tabs, message } from 'antd';
import { ScheduleOutlined, SettingOutlined, ClockCircleOutlined, SyncOutlined, FieldTimeOutlined } from '@ant-design/icons';
import { attendanceSettingsApi } from '../../../utils/api';
import AttendanceSettingsTab from '../../../components/hr/attendance/AttendanceSettingsTab';
import ShiftSettingsTab from '../../../components/hr/attendance/ShiftSettingsTab';
import ShiftRotationTab from '../../../components/hr/attendance/ShiftRotationTab';
import MonthlyLaborHoursTab from '../../../components/hr/attendance/MonthlyLaborHoursTab';
import { TableSkeleton } from '../../../components/SkeletonLoaders';

const { TabPane } = Tabs;

const AttendanceSettings = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'attendance');
  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState(null);
  const [shiftData, setShiftData] = useState(null);
  const [rotationData, setRotationData] = useState([]);

  useEffect(() => {
    if (activeTab === 'monthly-hours') {
      setLoading(false);
      return;
    }
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await attendanceSettingsApi.getSettings(activeTab);
      console.log('📥 Attendance Settings Response:', res);
      console.log('📥 Active Tab:', activeTab);
      
      // The response structure is: { active_tab, data: { attendance_setting, roles, ... } }
      // apiRequest returns the JSON directly, so res = { active_tab, data: {...} }
      const data = res?.data || res || {};
      console.log('📋 Parsed Data:', data);
      
      if (activeTab === 'attendance') {
        console.log('✅ Setting attendance data:', data);
        setAttendanceData(data);
      } else if (activeTab === 'shift') {
        console.log('✅ Setting shift data:', data);
        setShiftData(data);
      } else if (activeTab === 'shift-rotation') {
        // For shift-rotation, the response.data is directly an array
        const rotations = Array.isArray(data) ? data : (data ? [data] : []);
        console.log('✅ Setting rotation data:', rotations);
        setRotationData(rotations);
      }
    } catch (error) {
      console.error('خطا در دریافت تنظیمات:', error);
      message.error('خطا در دریافت تنظیمات حضور و غیاب');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  const handleDataUpdate = () => {
    loadData();
  };

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center shadow-lg">
                <ScheduleOutlined className="text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">
                  تنظیمات حضور و غیاب
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  مدیریت تنظیمات حضور و غیاب، شیفت‌ها و گردش شیفت‌ها
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {loading && activeTab !== 'monthly-hours' ? (
              <TableSkeleton rows={5} columns={4} />
            ) : (
              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                type="card"
                style={{ fontFamily: 'CustomFont' }}
              >
                <TabPane
                  tab={
                    <span style={{ fontFamily: 'CustomFont' }}>
                      <SettingOutlined className="mr-2" />
                      تنظیمات حضور و غیاب
                    </span>
                  }
                  key="attendance"
                >
                  <AttendanceSettingsTab
                    data={attendanceData}
                    onUpdate={handleDataUpdate}
                  />
                </TabPane>

                <TabPane
                  tab={
                    <span style={{ fontFamily: 'CustomFont' }}>
                      <FieldTimeOutlined className="mr-2" />
                      ساعات موظفی اداره کار
                    </span>
                  }
                  key="monthly-hours"
                >
                  <MonthlyLaborHoursTab />
                </TabPane>

                <TabPane
                  tab={
                    <span style={{ fontFamily: 'CustomFont' }}>
                      <ClockCircleOutlined className="mr-2" />
                      شیفت‌های کاری
                    </span>
                  }
                  key="shift"
                >
                  <ShiftSettingsTab
                    data={shiftData}
                    onUpdate={handleDataUpdate}
                  />
                </TabPane>

                <TabPane
                  tab={
                    <span style={{ fontFamily: 'CustomFont' }}>
                      <SyncOutlined className="mr-2" />
                      گردش شیفت‌ها
                    </span>
                  }
                  key="shift-rotation"
                >
                  <ShiftRotationTab
                    data={rotationData}
                    onUpdate={handleDataUpdate}
                  />
                </TabPane>
              </Tabs>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AttendanceSettings;

